import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { removeUserAvatar, updateUser, uploadUserAvatar, type UserDetail } from '../queries/admin';
import { Banner, Button, Input } from './ui';

/**
 * A person's name and picture, as support would fix them: a typo in the
 * name, a picture that has to go, or one they asked us to put up for them.
 * The picture is squared and shrunk to 512px here, so the API sees the
 * same thing the app sends and never a 12 MB phone photo.
 */

const OUTPUT = 512;

async function squared(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('That file did not open as an image.'));
      img.src = url;
    });
    const side = Math.min(image.naturalWidth, image.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser could not draw the picture.');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, (image.naturalWidth - side) / 2, (image.naturalHeight - side) / 2, side, side, 0, 0, OUTPUT, OUTPUT);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The picture could not be saved.'))), 'image/jpeg', 0.9);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function ProfileCard({ user }: { user: UserDetail }) {
  const client = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(user.name ?? '');
  useEffect(() => setName(user.name ?? ''), [user.name]);
  const dirty = name.trim() !== (user.name ?? '').trim();

  const refresh = (field: 'name' | 'avatar') => {
    track('Admin User Edited', { field });
    return Promise.all([
      client.invalidateQueries({ queryKey: ['admin', 'user', user.id] }),
      client.invalidateQueries({ queryKey: ['admin', 'users'] }),
    ]);
  };

  const rename = useMutation({
    mutationFn: () => updateUser(user.id, { name: name.trim() || null }),
    onSuccess: () => refresh('name'),
  });
  const picture = useMutation({
    mutationFn: async (file: File) => uploadUserAvatar(user.id, await squared(file)),
    onSuccess: () => refresh('avatar'),
  });
  const clearPicture = useMutation({
    mutationFn: () => removeUserAvatar(user.id),
    onSuccess: () => refresh('avatar'),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (dirty) rename.mutate();
  };
  const initial = (user.name || user.email).trim().charAt(0).toUpperCase();
  const failure = rename.error ?? picture.error ?? clearPicture.error;

  return (
    <div className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-start gap-6">
        <div className="flex items-center gap-4">
          {user.avatarUrl ? (
            <img src={user.avatarUrl} alt="" referrerPolicy="no-referrer" className="h-16 w-16 rounded-full object-cover" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-hairline bg-slate-800 text-h3 text-chalk">
              {initial}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) picture.mutate(file);
                event.target.value = '';
              }}
            />
            <Button size="sm" busy={picture.isPending} onClick={() => fileInput.current?.click()}>
              {user.avatarUrl ? 'Change picture' : 'Upload a picture'}
            </Button>
            {user.avatarUrl ? (
              <Button size="sm" variant="ghost" busy={clearPicture.isPending} onClick={() => clearPicture.mutate()}>
                Remove picture
              </Button>
            ) : null}
          </div>
        </div>

        <form onSubmit={submit} className="flex min-w-[16rem] flex-1 items-end gap-2">
          <div className="flex-1">
            <Input label="Name" value={name} maxLength={80} placeholder="No name" onChange={(event) => setName(event.target.value)} />
          </div>
          <Button type="submit" variant="primary" size="sm" busy={rename.isPending} disabled={!dirty}>
            Save
          </Button>
        </form>
      </div>
      <p className="mt-3 text-caption text-chalk-faint">
        What the person sees on their own account and what their viewers see on comments. Email belongs to the sign-in provider and is not editable here.
        {rename.isSuccess && !dirty ? <span className="ml-2 text-go">Saved.</span> : null}
      </p>
      {failure ? (
        <div className="mt-3">
          <Banner tone="danger">{errorMessage(failure)}</Banner>
        </div>
      ) : null}
    </div>
  );
}
